import React from 'react';
import { Link, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProfilePage from '../Settings/Profile/ProfilePage';
import SubUserPage from '../Settings/SubUser/SubUserPage';
import ProductSettingsPage from '../Settings/ProductSettings/ProductSettingsPage';
import AddressBillingPage from '../Settings/AddressBilling/AddressBillingPage';
import AddressSenderPage from '../Settings/AddressSender/AddressSenderPage';
import AddressRecipientPage from '../Settings/AddressRecipient/AddressRecipientPage';
import PasswordPage from '../Settings/Password/PasswordPage';
import WebTokenPage from '../Settings/Api/WebTokenPage';
import PaymentPage from '../Settings/Payment/PaymentPage';
import BillingPage from '../Settings/Billing/BillingPage';
import InvoicePage from '../Settings/Invoice/InvoicePage';
import InvoiceSettingsPage from '../Settings/Invoice/InvoiceSettingsPage';
import BoxPage from "../Settings/Box/BoxPage";
import LabelSettingsPage from "../Settings/LabelSettings/LabelSettingsPage";
import TagSettingsPage from "../Settings/TagSettings/TagSettingsPage";
import TimezoneSettingsPage from "../Settings/TimezoneSettings/TimezoneSettingsPage";
import ManifestSettingsPage from "../Settings/ManifestSettings/ManifestSettingsPage";
import PickupSettingsPage from "../Settings/PickupSettings/PickupSettingsPage";
import PickupAddressPage from "../Settings/PickupAddress/PickupAddressPage";
import QZTrayPage from '../Settings/QZTray/QZTrayPage';
import NotFoundPage from '../../pages/Auth/NotFoundPage';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import KeyIcon from '@mui/icons-material/Key';
import PaymentIcon from '@mui/icons-material/Payment';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LabelIcon from '@mui/icons-material/Label';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import MonitorWeightOutlinedIcon from '@mui/icons-material/MonitorWeightOutlined';
import Box from '@mui/material/Box';

const enterpriseSidebarNavButtonStyles = (isActive) => ({
    borderRadius: '10px',
    mb: 0.5,
    mx: 1,
    width: 'calc(100% - 16px)',
    padding: '10px 14px',
    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
    border: '1px solid',
    borderColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiListItemIcon-root': {
        color: isActive ? '#1976d2' : '#64748B',
        minWidth: '36px',
        transition: 'all 0.15s ease-in-out'
    },
    '& .MuiListItemText-primary': {
        color: isActive ? '#1976d2' : '#334155',
        fontWeight: isActive ? 700 : 500,
        fontSize: '13.5px',
        transition: 'all 0.15s ease-in-out'
    },
    '&:hover': {
        backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : '#F8FAFC',
        borderColor: isActive ? 'rgba(25, 118, 210, 0.2)' : '#F1F5F9',
        '& .MuiListItemIcon-root': { color: '#1976d2' },
        '& .MuiListItemText-primary': { color: '#1976d2' }
    }
});

const SettingsPage = (props) => {
    const { connectQZ, disconnectQZ } = props;
    const locations = useLocation();
    const currentPageActive = locations.pathname.replace('/', '');
    const profile = useSelector(state => state.auth);
    
    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
            <Box 
                className="scmaincard"
                sx={{
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%' // FIXED: Force master panel container card frame context to be explicit
                }}
            >
                {/* Main Hub Operational Branding Title Panel */}
                <Box 
                    className="sccardhead"
                    sx={{
                        p: 2.5,
                        borderBottom: '1px solid #E2E8F0',
                        backgroundColor: '#FFFFFF',
                        flexShrink: 0
                    }}
                >
                    <Typography 
                        variant="h5" 
                        component="h1" 
                        sx={{ 
                            fontWeight: 800, 
                            color: '#0F172A',
                            letterSpacing: '-0.5px',
                            margin: 0
                        }}
                    >
                        Settings
                    </Typography>               
                </Box>
                
                {/* Main operational viewport layer inside card bounds */}
                <Box 
                    className="sc--main-content--body" 
                    sx={{ 
                        p: 2.5, 
                        backgroundColor: '#FFFFFF',
                        flexGrow: 1,
                        overflow: 'hidden', // Lock browser outer overflow tracking
                        display: 'flex',
                        minHeight: 0
                    }}
                >
                    <Grid 
                        container 
                        spacing={3} 
                        className="sc--settings--menu" 
                        sx={{ 
                            height: '100%', 
                            minHeight: 0, 
                            width: 'calc(100% + 24px)', // FIXED: Counters the negative margin layouts built into MUI grid modules
                            margin: '-12px' 
                        }}
                    >
                        
                        {/* Left Hand Navigation Links Sidebar Panel Column */}
                        <Grid item xs={12} sm={12} md={4} lg={2.5} xl={2} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    width: '100%',
                                    borderRadius: '12px',
                                    borderColor: '#E2E8F0',
                                    backgroundColor: '#FFFFFF',
                                    py: 1,
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.02)',
                                    overflowY: 'auto',
                                    height: '100%'
                                }}
                            >
                                <List disablePadding>
                                    <ListItemButton 
                                        component={Link} 
                                        to="profile" 
                                        selected={currentPageActive === "settings/profile"}
                                        sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/profile")}
                                    >
                                        <ListItemIcon>
                                            <AccountCircleIcon sx={{ fontSize: '20px' }} />
                                        </ListItemIcon>
                                        <ListItemText primary="Profile" />
                                    </ListItemButton>
                                    
                                    {(profile && profile.profile && (profile.profile.id == profile.profile.parent_id)) && (
                                        <>
                                            <ListItemButton 
                                                component={Link} 
                                                to="subusers" 
                                                selected={currentPageActive === "settings/subusers"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/subusers")}
                                            >
                                                <ListItemIcon>
                                                    <SupervisedUserCircleIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Sub Users" />
                                            </ListItemButton>
                                            
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/productsettings" 
                                                selected={currentPageActive === "settings/productsettings"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/productsettings")}
                                            >
                                                <ListItemIcon>
                                                    <SettingsApplicationsIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Product Settings" />
                                            </ListItemButton>
                                        </>
                                    )}
                                    
                                    <ListItemButton 
                                        component={Link} 
                                        to="password" 
                                        selected={currentPageActive === "settings/password"}
                                        sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/password")}
                                    >
                                        <ListItemIcon>
                                            <LockClockOutlinedIcon sx={{ fontSize: '20px' }} />
                                        </ListItemIcon>
                                        <ListItemText primary="Password" />
                                    </ListItemButton>
                                    
                                    {(profile && profile.profile && (profile.profile.id == profile.profile.parent_id)) && (
                                        <>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/address-billing" 
                                                selected={currentPageActive === "settings/address-billing"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/address-billing")}
                                            >
                                                <ListItemIcon>
                                                    <AccountBalanceWalletIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Billing Address" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/address-sender" 
                                                selected={currentPageActive === "settings/address-sender"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/address-sender")}
                                            >
                                                <ListItemIcon>
                                                    <ForwardToInboxIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Sender Address" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/address-recipient" 
                                                selected={currentPageActive === "settings/address-recipient"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/address-recipient")}
                                            >
                                                <ListItemIcon>
                                                    <ContactMailIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Recipient Address" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/box" 
                                                selected={currentPageActive === "settings/box"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/box")}
                                            >
                                                <ListItemIcon>
                                                    <AllInboxIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Box Dimensions" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/manifest-settings" 
                                                selected={currentPageActive === "settings/manifest-settings"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/manifest-settings")}
                                            >
                                                <ListItemIcon>
                                                    <AssignmentTurnedInOutlinedIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Manifest Settings" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/pickup-settings" 
                                                selected={currentPageActive === "settings/pickup-settings"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/pickup-settings")}
                                            >
                                                <ListItemIcon>
                                                    <LocalShippingOutlinedIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Pickup Settings" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/label-settings" 
                                                selected={currentPageActive === "settings/label-settings"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/label-settings")}
                                            >
                                                <ListItemIcon>
                                                    <LabelIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Label Settings" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/tag-settings" 
                                                selected={currentPageActive === "settings/tag-settings"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/tag-settings")}
                                            >
                                                <ListItemIcon>
                                                    <TagOutlinedIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Tag Settings" />
                                            </ListItemButton>
                                        </>
                                    )}
                                    
                                    <ListItemButton 
                                        component={Link} 
                                        to="/settings/timezone-settings" 
                                        selected={currentPageActive === "settings/timezone-settings"}
                                        sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/timezone-settings")}
                                    >
                                        <ListItemIcon>
                                            <AccessTimeOutlinedIcon sx={{ fontSize: '20px' }} />
                                        </ListItemIcon>
                                        <ListItemText primary="Timezone Settings" />
                                    </ListItemButton>
                                    
                                    {(profile && profile.profile && (profile.profile.id == profile.profile.parent_id)) && (
                                        <>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/token" 
                                                selected={currentPageActive === "settings/token"}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/token")}
                                            >
                                                <ListItemIcon>
                                                    <KeyIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="API Token" />
                                            </ListItemButton>
                                            <ListItemButton 
                                                component={Link} 
                                                to="/settings/billing" 
                                                selected={(currentPageActive === "settings/billing" || currentPageActive === "settings/invoices" || currentPageActive === "settings/payment")}
                                                sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/billing" || currentPageActive === "settings/invoices" || currentPageActive === "settings/payment")}
                                            >
                                                <ListItemIcon>
                                                    <PaymentIcon sx={{ fontSize: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText primary="Billing" />
                                            </ListItemButton>
                                        </>
                                    )}
                                    
                                    <ListItemButton 
                                        component={Link} 
                                        to="/settings/qztray" 
                                        selected={currentPageActive === "settings/qztray"}
                                        sx={enterpriseSidebarNavButtonStyles(currentPageActive === "settings/qztray")}
                                    >
                                        <ListItemIcon>
                                            <MonitorWeightOutlinedIcon sx={{ fontSize: '20px' }} />
                                        </ListItemIcon>
                                        <ListItemText primary="QZ Tray" />
                                    </ListItemButton>
                                </List>
                            </Paper>
                        </Grid>
                        
                        {/* Right Hand Dynamic Sub-Page Routing Wrapper Window Panel */}
                        {/* FIXED: Applied display: 'flex', minHeight: 0 and overflow properties directly to lock layout stretching */}
                        <Grid item xs={12} sm={12} md={8} lg={9.5} xl={10} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    width: '100%',
                                    height: '100%',
                                    overflowY: 'auto', // FIXED: Confines scroll tracking cleanly within the internal layout container box boundaries
                                    borderRadius: '12px',
                                    borderColor: '#E2E8F0',
                                    backgroundColor: '#FFFFFF',
                                    p: 0,
                                    display: 'flex',
                                    flexDirection: 'column'
                                }} 
                            >
                                <Switch>
                                    <Redirect exact from="/settings" to="/settings/profile" />
                                    <Route path='/settings/profile' component={ProfilePage} />
                                    <Route path='/settings/subusers' component={SubUserPage} />
                                    <Route path='/settings/password' component={PasswordPage} />
                                    <Route path='/settings/address-billing' component={AddressBillingPage} />
                                    <Route path='/settings/address-sender' component={AddressSenderPage} />
                                    <Route path='/settings/address-recipient' component={AddressRecipientPage} />
                                    <Route path='/settings/token' component={WebTokenPage} />
                                    <Route path='/settings/label-settings' component={LabelSettingsPage} />
                                    <Route path='/settings/box' component={BoxPage} />
                                    <Route path='/settings/pickup-address' component={PickupAddressPage} />
                                    <Route path='/settings/productsettings' component={ProductSettingsPage} />
                                    <Route path='/settings/billing' component={BillingPage} />
                                    <Route path='/settings/manifest-settings' component={ManifestSettingsPage} />
                                    <Route path='/settings/pickup-settings' component={PickupSettingsPage} />
                                    <Route path='/settings/tag-settings' component={TagSettingsPage} />
                                    <Route path='/settings/timezone-settings' component={TimezoneSettingsPage} />
                                    <Route path='/settings/payment' component={PaymentPage} />
                                    <Route path='/settings/invoices' component={InvoicePage} />
                                    <Route path='/settings/invoice-settings' component={InvoiceSettingsPage} />
                                    <Route path='/settings/qztray' render={(props) => (
                                        <QZTrayPage 
                                            {...props} 
                                            connectQZ={connectQZ} 
                                            disconnectQZ={disconnectQZ} 
                                        />
                                        )} 
                                    />
                                    <Route path='*' component={NotFoundPage} />
                                </Switch>
                            </Paper>
                        </Grid>

                    </Grid>
                </Box>
            </Box>
        </Box>
    )
}

export default SettingsPage;